import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Badge } from './ui/badge';
import { 
  Building2, 
  Plus, 
  Edit, 
  Globe, 
  CheckCircle, 
  AlertTriangle,
  ExternalLink,
  XCircle,
  Loader2 
} from 'lucide-react';
import { projectId } from '../utils/supabase/info';
import { toast } from 'sonner';

interface ConsultingFirm {
  id: string;
  name: string;
  subdomain: string;
  createdAt?: string;
  companyCount?: number;
  companyLimitMultiplier?: number | null;
}

interface ConsultingFirmManagerProps {
  accessToken: string;
  isSystemAdmin?: boolean;
}

export function ConsultingFirmManager({ accessToken, isSystemAdmin }: ConsultingFirmManagerProps) {
  const [firms, setFirms] = useState<ConsultingFirm[]>([]);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedFirm, setSelectedFirm] = useState<ConsultingFirm | null>(null);
  const [formData, setFormData] = useState({ name: '', subdomain: '', companyLimitMultiplier: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [globalMultiplier, setGlobalMultiplier] = useState<number>(5);
  const [subdomainCheck, setSubdomainCheck] = useState<{
    checking: boolean;
    available: boolean | null;
    message: string;
  }>({ checking: false, available: null, message: '' });

  useEffect(() => {
    loadFirms();
    loadGlobalMultiplier();
  }, []);

  // Real-time subdomain availability checking
  useEffect(() => {
    const subdomain = formData.subdomain;
    
    // Skip check if empty, unchanged, or dialog is closed
    if (!subdomain || (!editDialogOpen && !createDialogOpen)) {
      setSubdomainCheck({ checking: false, available: null, message: '' });
      return;
    }

    // When editing, skip check if subdomain hasn't changed (don't show any badge)
    if (editDialogOpen && selectedFirm && subdomain === selectedFirm.subdomain) {
      console.log('[SubdomainCheck] Skipping check - subdomain unchanged:', { subdomain, original: selectedFirm.subdomain });
      setSubdomainCheck({ checking: false, available: null, message: '' });
      return;
    }

    console.log('[SubdomainCheck] Proceeding with check:', { subdomain, editing: editDialogOpen, original: selectedFirm?.subdomain });

    // Validate format first
    const subdomainRegex = /^[a-z0-9-]+$/;
    if (!subdomainRegex.test(subdomain)) {
      setSubdomainCheck({ 
        checking: false, 
        available: false, 
        message: 'Invalid format. Use lowercase letters, numbers, and hyphens only.' 
      });
      return;
    }

    // Debounce the API call
    const timer = setTimeout(async () => {
      setSubdomainCheck({ checking: true, available: null, message: 'Checking availability...' });
      
      try {
        const excludeFirmId = editDialogOpen && selectedFirm ? selectedFirm.id : undefined;
        const queryParam = excludeFirmId ? `?excludeFirmId=${excludeFirmId}` : '';
        
        const response = await fetch(
          `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/public/check-subdomain/${subdomain}${queryParam}`
        );
        
        if (!response.ok) {
          throw new Error('Failed to check subdomain');
        }
        
        const data = await response.json();
        
        setSubdomainCheck({
          checking: false,
          available: data.available,
          message: data.available 
            ? 'Available' 
            : data.error || 'Already taken',
        });
      } catch (err) {
        console.error('Subdomain check error:', err);
        setSubdomainCheck({
          checking: false,
          available: null,
          message: 'Unable to verify availability',
        });
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timer);
  }, [formData.subdomain, editDialogOpen, createDialogOpen, selectedFirm]);

  const loadGlobalMultiplier = async () => {
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/admin/company-limit-multiplier`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setGlobalMultiplier(data.multiplier);
      }
    } catch (err) {
      console.error('Error loading global multiplier:', err);
    }
  };

  const loadFirms = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/admin/consulting-firms`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        // Map the response to our interface
        const mappedFirms = (data.firms || []).map((firm: any) => ({
          id: firm.id,
          name: firm.name,
          subdomain: firm.subdomain,
          createdAt: firm.created_at,
          companyCount: firm.clientCompaniesCount,
          companyLimitMultiplier: firm.companyLimitMultiplier,
        }));
        setFirms(mappedFirms);
      } else {
        console.error('Failed to load consulting firms');
        setError('Failed to load consulting firms');
      }
    } catch (err) {
      console.error('Error loading firms:', err);
      setError('Error loading consulting firms');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setFormData({ name: '', subdomain: '', companyLimitMultiplier: '' });
    setSelectedFirm(null);
    setError('');
    setSubdomainCheck({ checking: false, available: null, message: '' });
    setCreateDialogOpen(true);
  };

  const handleEdit = (firm: ConsultingFirm) => {
    setFormData({ 
      name: firm.name, 
      subdomain: firm.subdomain,
      companyLimitMultiplier: firm.companyLimitMultiplier ? String(firm.companyLimitMultiplier) : ''
    });
    setSelectedFirm(firm);
    setError('');
    setSubdomainCheck({ checking: false, available: null, message: '' });
    setEditDialogOpen(true);
  };

  const handleSubmitCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      console.log('[ConsultingFirmManager] Creating firm:', {
        name: formData.name.trim(),
        subdomain: formData.subdomain.trim()
      });

      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/admin/consulting-firms`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            name: formData.name.trim(),
            subdomain: formData.subdomain.trim().toLowerCase(),
          }),
        }
      );

      console.log('[ConsultingFirmManager] Create response status:', response.status);

      // Handle non-JSON responses safely
      const contentType = response.headers.get('content-type') || '';
      let data;
      
      if (contentType.includes('application/json')) {
        data = await response.json();
      } else {
        const text = await response.text();
        console.error('[ConsultingFirmManager] Non-JSON response:', text.substring(0, 300));
        throw new Error(`Server returned non-JSON response (${response.status}): ${text.substring(0, 100)}`);
      }

      if (response.ok) {
        console.log('[ConsultingFirmManager] Firm created successfully:', data.firm);
        toast.success('Consulting firm created');
        setCreateDialogOpen(false);
        setSubdomainCheck({ checking: false, available: null, message: '' });
        loadFirms();
      } else {
        console.error('[ConsultingFirmManager] Create failed:', response.status, data);
        throw new Error(data.error || `Failed to create consulting firm (${response.status})`);
      }
    } catch (err: any) {
      console.error('[ConsultingFirmManager] Error creating firm:', err);
      const errorMessage = err.message || 'Failed to create consulting firm';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFirm) return;

    setSubmitting(true);
    setError('');

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/admin/consulting-firms/${selectedFirm.id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            name: formData.name.trim(),
            subdomain: formData.subdomain.trim().toLowerCase(),
          }),
        }
      );

      if (response.ok) {
        // Save custom multiplier if provided
        if (formData.companyLimitMultiplier !== '') {
          const multiplier = formData.companyLimitMultiplier ? parseFloat(formData.companyLimitMultiplier) : null;
          
          await fetch(
            `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/admin/firm/${selectedFirm.id}/company-limit-multiplier`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`,
              },
              body: JSON.stringify({ multiplier }),
            }
          );
        }
        
        toast.success('Consulting firm updated');
        setEditDialogOpen(false);
        setSubdomainCheck({ checking: false, available: null, message: '' });
        loadFirms();
      } else {
        const data = await response.json();
        throw new Error(data.error || 'Failed to update consulting firm');
      }
    } catch (err: any) {
      console.error('Error updating firm:', err);
      setError(err.message || 'Failed to update consulting firm');
      toast.error(err.message || 'Failed to update consulting firm');
    } finally {
      setSubmitting(false);
    }
  };

  const validateSubdomain = (subdomain: string): boolean => {
    // Subdomain must be lowercase, alphanumeric, and can contain hyphens
    const subdomainRegex = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
    return subdomainRegex.test(subdomain);
  };

  const isFormValid = (): boolean => {
    // When editing and subdomain hasn't changed, allow submission
    const isUnchangedSubdomain = editDialogOpen && selectedFirm && formData.subdomain === selectedFirm.subdomain;
    
    return (
      formData.name.trim().length > 0 &&
      formData.subdomain.trim().length > 0 &&
      validateSubdomain(formData.subdomain.trim().toLowerCase()) &&
      (isUnchangedSubdomain || subdomainCheck.available === true) && // Allow if unchanged OR available
      !subdomainCheck.checking // Not currently checking
    );
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center py-8 text-gray-500">
            Loading consulting firms...
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl flex items-center gap-2">
            <Building2 className="w-6 h-6" />
            Consulting Firms
          </h2>
          <p className="text-gray-600">
            Manage consulting firm tenants and their subdomains
          </p>
        </div>
        {isSystemAdmin && (
          <Button onClick={handleCreate}>
            <Plus className="w-4 h-4 mr-2" />
            Create Firm
          </Button>
        )}
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Firms List */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {firms.length === 0 ? (
          <Card className="col-span-full">
            <CardContent className="p-6">
              <div className="text-center py-8 text-gray-500">
                No consulting firms found. Create one to get started.
              </div>
            </CardContent>
          </Card>
        ) : (
          firms.map((firm) => (
            <Card key={firm.id} className="hover:shadow-md transition-shadow">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Building2 className="w-5 h-5" />
                      {firm.name}
                    </CardTitle>
                    <CardDescription className="flex items-center gap-2 mt-2">
                      <Globe className="w-4 h-4" />
                      {firm.subdomain}.pathkit.pro
                    </CardDescription>
                  </div>
                  {isSystemAdmin && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(firm)}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Firm ID:</span>
                    <Badge variant="outline" className="font-mono text-xs">
                      {firm.id.substring(0, 8)}...
                    </Badge>
                  </div>
                  {firm.companyCount !== undefined && (
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Client Companies:</span>
                      <Badge variant="secondary">{firm.companyCount}</Badge>
                    </div>
                  )}
                  {firm.createdAt && (
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Created:</span>
                      <span className="text-gray-700">
                        {new Date(firm.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                </div>
                <div className="mt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={() => window.open(`https://${firm.subdomain}.pathkit.pro`, '_blank')}
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Visit Portal
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Consulting Firm</DialogTitle>
            <DialogDescription>
              Create a new consulting firm tenant with its own subdomain
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmitCreate} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="create-name">Firm Name</Label>
              <Input
                id="create-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Acme Consulting"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="create-subdomain">Subdomain</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="create-subdomain"
                  value={formData.subdomain}
                  onChange={(e) => setFormData({ ...formData, subdomain: e.target.value.toLowerCase() })}
                  placeholder="e.g., acme"
                  required
                  pattern="[a-z0-9]+(?:-[a-z0-9]+)*"
                  title="Lowercase letters, numbers, and hyphens only"
                />
                <span className="text-gray-600 whitespace-nowrap">.pathkit.pro</span>
              </div>
              
              {/* Real-time availability indicator */}
              {formData.subdomain && (
                <div className="mt-2">
                  {subdomainCheck.checking ? (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Checking availability...</span>
                    </div>
                  ) : subdomainCheck.available === true ? (
                    <Badge variant="outline" className="bg-green-100 text-green-900 border-green-300">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      {subdomainCheck.message}
                    </Badge>
                  ) : subdomainCheck.available === false ? (
                    <Badge variant="outline" className="bg-red-100 text-red-900 border-red-300">
                      <XCircle className="h-3 w-3 mr-1" />
                      {subdomainCheck.message}
                    </Badge>
                  ) : null}
                </div>
              )}
              
              {formData.subdomain && !validateSubdomain(formData.subdomain) && !subdomainCheck.checking && (
                <p className="text-sm text-red-600">
                  Subdomain must contain only lowercase letters, numbers, and hyphens
                </p>
              )}
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setCreateDialogOpen(false)}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={submitting || !isFormValid()}>
                {submitting ? 'Creating...' : 'Create Firm'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Consulting Firm</DialogTitle>
            <DialogDescription>
              Update consulting firm name and subdomain
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmitEdit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Firm Name</Label>
              <Input
                id="edit-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Acme Consulting"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-subdomain">Subdomain</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="edit-subdomain"
                  value={formData.subdomain}
                  onChange={(e) => setFormData({ ...formData, subdomain: e.target.value.toLowerCase() })}
                  placeholder="e.g., acme"
                  required
                  pattern="[a-z0-9]+(?:-[a-z0-9]+)*"
                  title="Lowercase letters, numbers, and hyphens only"
                />
                <span className="text-gray-600 whitespace-nowrap">.pathkit.pro</span>
              </div>
              
              {/* Real-time availability indicator */}
              {formData.subdomain && (
                <div className="mt-2">
                  {subdomainCheck.checking ? (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Checking availability...</span>
                    </div>
                  ) : subdomainCheck.available === true ? (
                    <Badge variant="outline" className="bg-green-100 text-green-900 border-green-300">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      {subdomainCheck.message}
                    </Badge>
                  ) : subdomainCheck.available === false ? (
                    <Badge variant="outline" className="bg-red-100 text-red-900 border-red-300">
                      <XCircle className="h-3 w-3 mr-1" />
                      {subdomainCheck.message}
                    </Badge>
                  ) : null}
                </div>
              )}
              
              {formData.subdomain && !validateSubdomain(formData.subdomain) && !subdomainCheck.checking && (
                <p className="text-sm text-red-600">
                  Subdomain must contain only lowercase letters, numbers, and hyphens
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-multiplier">Company Limit Multiplier (Optional)</Label>
              <Input
                id="edit-multiplier"
                type="number"
                min={1}
                max={100}
                value={formData.companyLimitMultiplier}
                onChange={(e) => setFormData({ ...formData, companyLimitMultiplier: e.target.value })}
                placeholder={`Default: ${globalMultiplier}x`}
              />
              <p className="text-xs text-muted-foreground">
                Leave empty to use global default ({globalMultiplier}x). Custom multiplier allows this firm to have different company limits per user.
              </p>
            </div>

            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                <strong>Warning:</strong> Changing the subdomain will affect all users accessing this consulting firm.
                Make sure to communicate this change to all consultants and clients.
              </AlertDescription>
            </Alert>

            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setEditDialogOpen(false)}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={submitting || !isFormValid()}>
                {submitting ? 'Updating...' : 'Update Firm'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}